import React, { useEffect, useMemo, useState } from 'react';
import './Schedule.css';

/** ===== CONFIG ===== */
const GAS_ENDPOINT = "https://script.google.com/macros/s/AKfycby5U9vpyYudtx4xt_XPmpScb4sfPnhBCTKp2LHTLypdDwAF3YH_uOCBnRw0vf6gl1wb/exec";
const SHARED_SECRET = "change-me-strong-secret"; // "" if disabled on backend

// Lesson type -> minutes (grid remains 40)
const DURATION_MAP = {
  single: 40,       // שיעור רגיל
  one_half: 60,     // שיעור וחצי
  double: 80        // שיעור כפול
};

export default function Schedule() {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [studentName, setStudentName] = useState('');
  const [studentPhone, setStudentPhone] = useState('');
  const [pickupAddress, setPickupAddress] = useState(''); // optional
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Validation functions
  const validateIsraeliPhone = (phone) => {
    // Remove all non-digit characters (spaces, dashes, parentheses, etc.)
    const cleanPhone = phone.replace(/\D/g, '');
    // Check if it's exactly 10 digits and starts with 05
    return cleanPhone.length === 10 && cleanPhone.startsWith('05');
  };

  const validateForm = () => {
    const errors = {};
    
    // Required field validations - only mark as invalid (red border) without error text
    if (!selectedDate.trim()) {
      errors.selectedDate = true;
    }
    
    if (!lessonType) {
      errors.lessonType = true;
    }
    
    if (!selectedTime.trim()) {
      errors.selectedTime = true;
    }
    
    if (!studentName.trim()) {
      errors.studentName = true;
    }
    
    if (!studentPhone.trim()) {
      errors.studentPhone = true;
    } else if (!validateIsraeliPhone(studentPhone)) {
      errors.studentPhone = true;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Enhanced scroll handling for #schedule anchor
  useEffect(() => {
    const handleScheduleScroll = () => {
      if (window.location.hash === '#schedule') {
        // Clear any existing hash to prevent conflicts
        const originalHash = window.location.hash;
        window.history.replaceState(null, null, ' ');
        
        // Wait for DOM to be fully ready
        const scrollToSchedule = () => {
          const element = document.getElementById('schedule');
          if (element) {
            // Get the element's position
            const elementRect = element.getBoundingClientRect();
            const elementTop = elementRect.top + window.pageYOffset;
            
            // Account for any fixed headers or navigation
            const offset = 80; // Adjust this value based on your header height
            
            // Scroll to the element with proper offset
            window.scrollTo({
              top: elementTop - offset,
              behavior: 'smooth'
            });
            
            // Restore the hash after scrolling
            setTimeout(() => {
              window.history.replaceState(null, null, originalHash);
            }, 100);
          }
        };
        
        // Try multiple times with increasing delays to ensure DOM is ready
        setTimeout(scrollToSchedule, 50);
        setTimeout(scrollToSchedule, 200);
        setTimeout(scrollToSchedule, 500);
      }
    };
    
    // Handle initial load
    handleScheduleScroll();
    
    // Handle hash changes (when user navigates with #schedule)
    const handleHashChange = () => {
      if (window.location.hash === '#schedule') {
        handleScheduleScroll();
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const [lessonType, setLessonType] = useState(''); // '', then single | one_half | double
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [versionInfo, setVersionInfo] = useState('');

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const maxDateStr = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    return d.toISOString().split('T')[0];
  }, []);

  // Hide past times if booking "today"
  function filterPastTimesIfToday(dateStr, slots) {
    if (dateStr !== today) return slots;
    const now = new Date();
    const curHH = String(now.getHours()).padStart(2, '0');
    const curMM = String(now.getMinutes()).padStart(2, '0');
    const current = `${curHH}:${curMM}`;
    return slots.filter(hhmm => hhmm >= current);
  }

  // Probe backend version (for sanity)
  useEffect(() => {
    (async () => {
      try {
        const url = new URL(GAS_ENDPOINT);
        if (SHARED_SECRET) url.searchParams.set('secret', SHARED_SECRET);
        const res = await fetch(url.toString());
        const data = await res.json().catch(() => ({}));
        if (data?.version) setVersionInfo(data.version);
      } catch {}
    })();
  }, []);

  // Load slots when date or lessonType changes
  useEffect(() => {
    if (!selectedDate || !lessonType) return;
    const durationMinutes = DURATION_MAP[lessonType] || 40;

    (async () => {
      setLoadingSlots(true);
      setErrorMsg('');
      setAvailableSlots([]);
      setSelectedTime('');

      try {
        const url = new URL(GAS_ENDPOINT);
        url.searchParams.set('action', 'slots');
        url.searchParams.set('date', selectedDate);
        url.searchParams.set('durationMinutes', String(durationMinutes));
        if (SHARED_SECRET) url.searchParams.set('secret', SHARED_SECRET);

        const res = await fetch(url.toString());
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || 'Failed to load slots');

        const filtered = filterPastTimesIfToday(selectedDate, data.slots || []);
        setAvailableSlots(filtered);
      } catch (e) {
        console.error(e);
        setErrorMsg('שגיאה בטעינת הזמנים');
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, [selectedDate, lessonType, today]);

  async function onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    setAttemptedSubmit(true);
    
    // Validate form before submission
    if (!validateForm()) {
      // Just show red fields, no focus or scrolling
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const durationMinutes = DURATION_MAP[lessonType] || 40;
      const body = {
        date: selectedDate,
        time: selectedTime,
        studentName,
        studentPhone,
        pickupAddress,    // optional
        durationMinutes
      };
      if (SHARED_SECRET) body.secret = SHARED_SECRET;

      // text/plain avoids preflight; application/json also fine
      const res = await fetch(GAS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (!data.ok) {
        const msg = data.error || 'Booking failed';
        if (/slot already taken/i.test(msg)) {
          alert('מצטערים, השעה כבר נתפסה. אנא בחר/י שעה אחרת.');
        } else {
          alert('שגיאה בקביעת השיעור. נסו שוב.');
        }
        return;
      }

      alert(`השיעור נקבע!\nתאריך: ${selectedDate}\nשעה: ${selectedTime}`);

      // reset
      setSelectedDate('');
      setSelectedTime('');
      setStudentName('');
      setStudentPhone('');
      setPickupAddress('');
      setAvailableSlots([]);
      setLessonType('');
      setAttemptedSubmit(false);
      setValidationErrors({});
    } catch (e) {
      console.error(e);
      setErrorMsg('שגיאה בקביעת השיעור. נסו שוב.');
    } finally {
      setSubmitting(false);
    }
  }

  const durationLabel = {
    single: "שיעור רגיל",
    one_half: "שיעור וחצי",
    double: "שיעור כפול"
  };

  return (
    <section id="schedule" className="section schedule">
      <div className="container">
        <h2 className="section-title">קבע שיעור</h2>

        <div className="booking-form-container">
          <form onSubmit={onSubmit} className="booking-form" noValidate>
            <div className="form-row">
              <div className="form-group">
                <label>תאריך השיעור *</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => { 
                    setSelectedDate(e.target.value); 
                    setSelectedTime(''); 
                    // Clear validation error when user starts typing
                    if (validationErrors.selectedDate) {
                      setValidationErrors(prev => ({ ...prev, selectedDate: false }));
                    }
                  }}
                  min={today}
                  max={maxDateStr}
                  className={validationErrors.selectedDate ? 'invalid' : ''}
                />
              </div>

              <div className="form-group">
                <label>סוג שיעור *</label>
                <select
                  value={lessonType}
                  onChange={(e) => {
                    setLessonType(e.target.value);
                    // Clear validation error when user selects
                    if (validationErrors.lessonType) {
                      setValidationErrors(prev => ({ ...prev, lessonType: false }));
                    }
                  }}
                  className={validationErrors.lessonType ? 'invalid' : ''}
                >
                  <option value="" disabled hidden>בחר סוג שיעור</option>
                  <option value="single">{durationLabel.single}</option>
                  <option value="one_half">{durationLabel.one_half}</option>
                  <option value="double">{durationLabel.double}</option>
                </select>
              </div>

              <div className="form-group">
                <label>שעת השיעור *</label>
                {loadingSlots ? (
                  <div className="loading-slots">טוען זמנים זמינים...</div>
                ) : availableSlots.length ? (
                  <select
                    value={selectedTime}
                    onChange={(e) => {
                      setSelectedTime(e.target.value);
                      // Clear validation error when user selects
                      if (validationErrors.selectedTime) {
                        setValidationErrors(prev => ({ ...prev, selectedTime: false }));
                      }
                    }}
                    disabled={!selectedDate}
                    className={validationErrors.selectedTime ? 'invalid' : ''}
                  >
                    <option value="">בחר שעה</option>
                    {availableSlots.map((t) => (
                      <option key={t} value={t}>{t}</option>   
                    ))}
                  </select>
                ) : selectedDate ? (
                  <select disabled>
                    <option value="">אין שעות זמינות</option>
                  </select>
                ) : (
                  <select disabled>
                    <option value="">בחר תאריך</option>
                  </select>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>שם התלמיד *</label>
                <input
                  type="text"
                  value={studentName}
                  onChange={(e) => {
                    setStudentName(e.target.value);
                    // Clear validation error when user starts typing
                    if (validationErrors.studentName) {
                      setValidationErrors(prev => ({ ...prev, studentName: false }));
                    }
                  }}
                  className={validationErrors.studentName ? 'invalid' : ''}
                />
              </div>

              <div className="form-group">
                <label>טלפון *</label>
                <input
                  type="tel"
                  value={studentPhone}
                  onChange={(e) => {
                    setStudentPhone(e.target.value);
                    // Clear validation error when user starts typing
                    if (validationErrors.studentPhone) {
                      setValidationErrors(prev => ({ ...prev, studentPhone: false }));
                    }
                  }}
                  placeholder="0501234567"
                  className={validationErrors.studentPhone ? 'invalid' : ''}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>כתובת איסוף (לא חובה)</label>
                <input
                  type="text"
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  placeholder="למשל: רוטשילד 12, תל אביב"
                />
              </div>
            </div>


            <button
              type="submit"
              className="book-lesson-btn"
              disabled={submitting}
              onClick={() => setAttemptedSubmit(true)}
            >
              {submitting ? 'קובע שיעור...' : 'קבע שיעור'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
